/**
 * Airtable shipment receiving + Shopify sync result shapes.
 * Single source for backend and frontend; nullability is permissive at API boundaries.
 */

export interface AirtableShipment {
  id: string;
  name: string;
  description?: string | null;
  receivedInSF?: string | null;
  etaSF?: string | null;
  shipmentItemIds?: string[];
}

export interface AirtableShipmentItem {
  id: string;
  name?: string;
  sku?: string;
  quantityShipped: number;
  quantityRemaining?: number | null;
  inventoryImageUrl?: string | null;
  purchaseOrderItemId?: string | null;
  purchaseOrderNumber?: string | null;
  shipmentId?: string | null;
  shipmentName?: string | null;
  inventoryReceivedItemIds?: string[];
  totalReceivedDirect?: number | null;
  totalReceivedPOLevel?: number | null;
}

export interface AirtableInventoryReceivedItem {
  id: string;
  quantity: number;
  quantityDamaged?: number;
  purchaseOrderItemId?: string | null;
  shipmentItemId?: string | null;
  sku?: string;
  createdAt: string;
  receivedByName?: string | null;
  receivedByEmail?: string | null;
  rolledBackAt?: string | null;
  rolledBackByName?: string | null;
  rolledBackByEmail?: string | null;
}

export interface ReceivingActor {
  name: string;
  email: string;
}

export interface CreateInventoryReceivedItemPayload {
  purchaseOrderItemId: string;
  shipmentItemId: string;
  quantity: number;
  quantityDamaged?: number;
}

export interface ShipmentsListResponse {
  shipments: AirtableShipment[];
}

export interface ShipmentDetailResponse {
  shipment: AirtableShipment;
  shipmentItems: AirtableShipmentItem[];
}

export interface ShipmentItemDetailResponse {
  shipmentItem: AirtableShipmentItem;
  receivedItems: AirtableInventoryReceivedItem[];
  hasLegacyReceivedItems: boolean;
}

export interface InventoryAdjustmentResult {
  createdAt?: string;
  reason?: string;
  referenceDocumentUri?: string;
  changes?: Array<{ name: string; delta: number }>;
}

export interface TagResult {
  tagAdded: boolean;
  alreadyTagged?: boolean;
  existingTags?: string[];
  error?: string;
  productNotFound?: boolean;
  lifecyclePromoted?: boolean;
  promotedVariantIds?: string[];
}

export const RECEIVING_WRITE_STEPS = {
  lookup: 'lookup',
  supabase: 'supabase',
  shopify: 'shopify',
} as const;

export type ReceivingWriteStep = (typeof RECEIVING_WRITE_STEPS)[keyof typeof RECEIVING_WRITE_STEPS];

export interface ReceivingWriteErrorBody {
  error: string;
  step: ReceivingWriteStep;
}

export interface ShopifySyncResult {
  success: boolean;
  /** Set when Shopify sync did not complete; receipt may still have been saved. */
  step?: typeof RECEIVING_WRITE_STEPS.shopify;
  inventoryResult?: InventoryAdjustmentResult;
  tagResult?: TagResult;
  shopifyUpdateRecordId?: string;
  error?: string;
}

export interface CreateInventoryReceivedItemResult {
  receivedItem: AirtableInventoryReceivedItem;
  shopifySync?: ShopifySyncResult;
}

export interface ShopifyRollbackResult {
  applied: boolean;
  inventoryReversed?: boolean;
  tagRemoved?: boolean;
  skippedReason?: string;
  shopifyUpdateRecordId?: string;
  error?: string;
  step?: typeof RECEIVING_WRITE_STEPS.shopify;
}

export interface RollbackInventoryReceivedItemPayload {
  shipmentItemId: string;
}

export interface RollbackInventoryReceivedItemResult {
  receivedItem: AirtableInventoryReceivedItem;
  shopifyRollback: ShopifyRollbackResult;
}

/** Alias used by the frontend API module. */
export type CreateInventoryReceivedItemResponse = CreateInventoryReceivedItemResult;

export interface ShopifyUpdateSuccess {
  inventoryResult: InventoryAdjustmentResult;
  tagResult: TagResult;
  sku: string | null;
  quantity: number;
}

export interface ShopifyUpdateError {
  error: string;
  timestamp: string;
}

export type ShopifyUpdateData = ShopifyUpdateSuccess | ShopifyUpdateError;
